import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { 
  ArrowLeft, Play, Star, Trophy, ExternalLink, Timer, 
  User, Calendar, Lightbulb, X, GripVertical, Clock,
  RefreshCw, ChevronDown, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';
import { 
  getSlide, updateSlide, loadCompaniesToSlide, 
  toggleCompanyStar, setCompanyWinner, updateSlideJudge,
  updateSlideDecision, getSlideshowJudges, getSlideshowEvents
} from '../services/api';

// Confetti effect
const fireConfetti = (intensity = 'medium') => {
  const defaults = {
    spread: 360,
    ticks: 50,
    gravity: 0.8,
    decay: 0.9,
    startVelocity: 15,
    colors: ['#FFD700', '#FFA500', '#FF6347', '#00FF00', '#00CED1', '#1E90FF']
  };

  const configs = {
    light: { particleCount: 30, scalar: 0.8 },
    medium: { particleCount: 60, scalar: 1 },
    heavy: { particleCount: 150, scalar: 1.2 }
  };

  confetti({
    ...defaults,
    ...configs[intensity],
    origin: { x: Math.random(), y: Math.random() * 0.5 }
  });
};

// Timer Component
const TimerDisplay = ({ duration, isRunning, onToggle, onReset, onChangeDuration }) => {
  const [remaining, setRemaining] = useState(duration);
  
  useEffect(() => {
    setRemaining(duration);
  }, [duration]);

  useEffect(() => {
    let interval;
    if (isRunning && remaining > 0) {
      interval = setInterval(() => {
        setRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, remaining]);

  const progress = (remaining / duration) * 100;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-1">
        <Button
          size="sm"
          variant={duration === 60 ? 'default' : 'outline'}
          onClick={() => onChangeDuration(60)}
          className={duration === 60 ? 'bg-blue-600' : 'border-slate-600 text-slate-400'}
        >
          60s
        </Button>
        <Button
          size="sm"
          variant={duration === 90 ? 'default' : 'outline'}
          onClick={() => onChangeDuration(90)}
          className={duration === 90 ? 'bg-blue-600' : 'border-slate-600 text-slate-400'}
        >
          90s
        </Button>
      </div>
      
      <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-4 py-2">
        <Timer className="w-4 h-4 text-slate-400" />
        <span className="font-mono text-lg text-white">
          {minutes}:{seconds.toString().padStart(2, '0')}
        </span>
        <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all ${remaining < 10 ? 'bg-red-500' : 'bg-blue-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      <Button
        size="sm"
        variant="outline"
        onClick={onToggle}
        className="border-slate-600 text-slate-300"
      >
        {isRunning ? 'Pause' : 'Start'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onReset}
        className="text-slate-400"
      >
        <RefreshCw className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Judge Selector
const JudgeSelector = ({ selectedJudge, onSelect }) => {
  const [judges, setJudges] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const loadJudges = async () => {
      try {
        const data = await getSlideshowJudges();
        setJudges(data);
      } catch (error) {
        console.error('Error loading judges:', error);
      }
    };
    loadJudges();
  }, []);

  const selectedJudgeData = judges.find(j => j._id === selectedJudge);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 bg-slate-800 hover:bg-slate-700 rounded-lg px-4 py-2 transition-colors"
      >
        {selectedJudgeData ? (
          <>
            <img
              src={selectedJudgeData.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedJudgeData.name)}&background=4F46E5&color=fff`}
              alt={selectedJudgeData.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="text-left">
              <p className="text-sm font-medium text-white">{selectedJudgeData.name}</p>
              <p className="text-xs text-slate-400">{selectedJudgeData.title}</p>
            </div>
          </>
        ) : (
          <>
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
              <User className="w-4 h-4 text-slate-400" />
            </div>
            <span className="text-sm text-slate-400">Select Judge</span>
          </>
        )}
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
          {judges.map((judge) => (
            <button
              key={judge._id}
              onClick={() => {
                onSelect(judge._id);
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700 transition-colors text-left"
            >
              <img
                src={judge.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(judge.name)}&background=4F46E5&color=fff`}
                alt={judge.name}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-white">{judge.name}</p>
                <p className="text-xs text-slate-400">{judge.title} @ {judge.company}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Companies Tab Component
const CompaniesTab = ({ companies, slideId, onUpdate }) => {
  const [loading, setLoading] = useState({});

  const handleStar = async (companyId) => {
    setLoading(prev => ({ ...prev, [companyId]: true }));
    try {
      await toggleCompanyStar(slideId, companyId);
      fireConfetti('light');
      onUpdate();
    } catch (error) {
      console.error('Error toggling star:', error);
    } finally {
      setLoading(prev => ({ ...prev, [companyId]: false }));
    }
  };

  const handleWinner = async (companyId, rank) => {
    setLoading(prev => ({ ...prev, [`winner-${companyId}`]: true }));
    try {
      await setCompanyWinner(slideId, companyId, rank);
      fireConfetti('heavy');
      onUpdate();
    } catch (error) {
      console.error('Error setting winner:', error);
    } finally {
      setLoading(prev => ({ ...prev, [`winner-${companyId}`]: false }));
    }
  };

  return (
    <div className="space-y-2">
      {/* Header Row */}
      <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider border-b border-slate-800">
        <div className="col-span-1">#</div>
        <div className="col-span-2">Founder</div>
        <div className="col-span-2">Company</div>
        <div className="col-span-5">Solution Statement</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>

      {/* Company Rows */}
      {companies.map((company, index) => (
        <div 
          key={company.id}
          className={`grid grid-cols-12 gap-4 px-4 py-3 rounded-lg transition-all ${
            company.is_winner 
              ? 'bg-yellow-500/10 border border-yellow-500/30' 
              : company.is_starred 
                ? 'bg-blue-500/10 border border-blue-500/20' 
                : 'bg-slate-800/50 hover:bg-slate-800'
          }`}
        >
          <div className="col-span-1 flex items-center gap-2">
            <button
              onClick={() => handleStar(company.id)}
              disabled={loading[company.id]}
              className="transition-transform hover:scale-110"
            >
              <Star 
                className={`w-5 h-5 ${
                  company.is_starred 
                    ? 'text-yellow-400 fill-yellow-400' 
                    : 'text-slate-500 hover:text-yellow-400'
                }`}
              />
            </button>
            <span className="text-slate-500 text-sm">{index + 1}</span>
          </div>
          
          <div className="col-span-2 text-white font-medium truncate">
            {company.founder_name || 'Unknown'}
          </div>
          
          <div className="col-span-2 flex items-center gap-2">
            <span className="text-white truncate">{company.company_name}</span>
            {company.is_winner && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                <Trophy className="w-3 h-3 mr-1" />
                {company.winner_rank === 1 ? '1st' : company.winner_rank === 2 ? '2nd' : '3rd'}
              </Badge>
            )}
          </div>
          
          <div className="col-span-5 text-slate-400 text-sm line-clamp-2">
            {company.solution_statement || 'No solution statement'}
          </div>
          
          <div className="col-span-2 flex items-center justify-end gap-2">
            {company.website_url && (
              <a
                href={company.website_url.startsWith('http') ? company.website_url : `https://${company.website_url}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-slate-400 hover:text-blue-400 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <Button
              size="sm"
              variant={company.is_winner ? 'default' : 'outline'}
              onClick={() => handleWinner(company.id, 1)}
              disabled={loading[`winner-${company.id}`]}
              className={company.is_winner 
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                : 'border-slate-600 text-slate-400 hover:text-yellow-400 hover:border-yellow-500'
              }
            >
              <Trophy className="w-3 h-3 mr-1" />
              Winner
            </Button>
          </div>
        </div>
      ))}

      {companies.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p>No companies loaded. Click "Load from Airtable" to import companies.</p>
        </div>
      )}
    </div>
  );
};

// Questions Tab Component
const QuestionsTab = ({ questions }) => {
  return (
    <div className="space-y-3">
      {questions.map((question, index) => (
        <div 
          key={question.id}
          className="flex items-start gap-4 bg-slate-800/50 rounded-lg p-4"
        >
          <div className="flex items-center gap-2 text-slate-500">
            <GripVertical className="w-4 h-4" />
          </div>
          
          <div className="flex-1">
            <p className="text-white mb-2">{question.text}</p>
            {question.notes && (
              <p className="text-sm text-slate-400 italic">{question.notes}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-slate-400">
            <Clock className="w-4 h-4" />
            <span className="text-sm">{question.time_seconds}s</span>
          </div>
        </div>
      ))}

      {questions.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p>No questions configured for this slide.</p>
        </div>
      )}
    </div>
  );
};

// Decision Tab Component
const DecisionTab = ({ decisions, slideId, onUpdate }) => {
  const [loading, setLoading] = useState(false);

  const handleSelect = async (decisionId) => {
    setLoading(true);
    try {
      await updateSlideDecision(slideId, decisionId);
      onUpdate();
    } catch (error) {
      console.error('Error updating decision:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'x': return <X className="w-6 h-6" />;
      case 'user': return <User className="w-6 h-6" />;
      case 'calendar': return <Calendar className="w-6 h-6" />;
      case 'lightbulb': return <Lightbulb className="w-6 h-6" />;
      default: return null;
    }
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-white mb-6 text-center">What's the next step?</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {decisions.map((decision) => (
          <button
            key={decision.id}
            onClick={() => handleSelect(decision.id)}
            disabled={loading}
            className={`p-6 rounded-xl border-2 transition-all ${
              decision.is_selected
                ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600 hover:text-white'
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              {getIcon(decision.icon)}
              <span className="font-medium text-sm text-center">{decision.label}</span>
              {decision.is_selected && (
                <Check className="w-5 h-5 text-blue-400" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Review Slide Editor
const ReviewSlideEditor = () => {
  const { id, slideId } = useParams();
  const navigate = useNavigate();
  const [slide, setSlide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');
  const [timerDuration, setTimerDuration] = useState(60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    loadSlide();
  }, [slideId]);

  const loadSlide = async () => {
    try {
      const data = await getSlide(slideId);
      setSlide(data);
      if (data.content?.timer_duration) {
        setTimerDuration(data.content.timer_duration);
      }
      if (data.content?.active_tab) {
        setActiveTab(data.content.active_tab);
      }
    } catch (error) {
      console.error('Error loading slide:', error);
      navigate(`/admin/slideshows/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      await loadCompaniesToSlide(slideId);
      loadSlide();
    } catch (error) {
      console.error('Error loading companies:', error);
      alert('Error loading companies from Airtable');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleJudgeSelect = async (judgeId) => {
    try {
      await updateSlideJudge(slideId, judgeId);
      loadSlide();
    } catch (error) {
      console.error('Error updating judge:', error);
    }
  };

  const handleTimerDurationChange = async (duration) => {
    setTimerDuration(duration);
    setTimerRunning(false);
    try {
      await updateSlide(slideId, {
        content: { ...slide.content, timer_duration: duration }
      });
    } catch (error) {
      console.error('Error updating timer:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="slideshows">
        <div className="text-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading slide...</p>
        </div>
      </AdminLayout>
    );
  }

  const content = slide?.content || {};
  const companies = content.companies || [];
  const questions = content.questions || [];
  const decisions = content.decisions || [];

  return (
    <AdminLayout currentPage="slideshows">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/admin/slideshows/${id}`)}
            className="text-slate-400 hover:text-white p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold text-white">{slide?.title || 'Review Slide'}</h2>
            <p className="text-slate-400 text-sm">Company Review Slide</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <JudgeSelector 
            selectedJudge={content.judge_id}
            onSelect={handleJudgeSelect}
          />
          
          <TimerDisplay
            duration={timerDuration}
            isRunning={timerRunning}
            onToggle={() => setTimerRunning(!timerRunning)}
            onReset={() => setTimerRunning(false)}
            onChangeDuration={handleTimerDurationChange}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {['companies', 'questions', 'decision'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              {tab}
              {tab === 'companies' && companies.length > 0 && (
                <span className="ml-2 text-xs bg-slate-700 px-1.5 py-0.5 rounded">
                  {companies.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'companies' && (
          <Button
            onClick={handleLoadCompanies}
            disabled={loadingCompanies}
            variant="outline"
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingCompanies ? 'animate-spin' : ''}`} />
            {loadingCompanies ? 'Loading...' : 'Load from Airtable'}
          </Button>
        )}
      </div>

      {/* Tab Content */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-6">
          {activeTab === 'companies' && (
            <CompaniesTab 
              companies={companies} 
              slideId={slideId}
              onUpdate={loadSlide}
            />
          )}
          {activeTab === 'questions' && (
            <QuestionsTab questions={questions} />
          )}
          {activeTab === 'decision' && (
            <DecisionTab 
              decisions={decisions}
              slideId={slideId}
              onUpdate={loadSlide}
            />
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default ReviewSlideEditor;
